import {
  type Dispatch,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
} from "react";
import styled, { type CSSProperties } from "styled-components";
import { spacing } from "src/design-system/tokens/spacing";
import { Blanket } from "src/components/Blanket/Blanket";
import { layers } from "src/design-system/tokens/layers";
import color from "src/design-system/tokens/color";
import { border_radius } from "src/design-system/tokens/border";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { tw } from "src/design-system/tw";
import { Button } from "src/design-system/components/Button/Button";
import { BlurAwayListener } from "../../../components/BlurAwayListener/BlurAwayListener";
import { Drawer2 } from "../../../components/Drawer2/Drawer2";
import { Drawer2Actions } from "../../../components/Drawer2/Drawer2Actions/Drawer2Actions";
import { Drawer2Content } from "../../../components/Drawer2/Drawer2Content/Drawer2Content";
import { Drawer2Title } from "../../../components/Drawer2/Drawer2Title/Drawer2Title";
import {
  Dropdown,
  type DropdownAnchor,
} from "../../../components/Dropdown/Dropdown";
import { ComboboxInput } from "./ComboboxInput";

export type Props = PropsWithChildren<{
  id: string;
  icon?: ReactElement;
  inputText?: string;
  label: string | ReactNode;
  ariaLabel: string;
  onClickOpen?: () => void; // This might include specialized analytics logging.
  ctaMessage?: string;
  onClickCta?: () => void;
  children: ReactNode;
  isChildExpanded: boolean;
  setIsChildExpanded: Dispatch<SetStateAction<boolean>>;
  isDisabledCta?: boolean;
  showMobileControls?: boolean;
  isDoubleViewCalendar?: boolean;
  showInput?: boolean;
  onBackgroundClick?: () => void;
  onDismissClick?: () => void;
  focusRef?: React.RefObject<HTMLButtonElement | null>;
  maxDropDownWidth?: CSSProperties["maxWidth"];
  dropdownWidth?: CSSProperties["width"];
  pillBorderColor?: CSSProperties["color"];
  disabled?: boolean;
  disabledIcon?: ReactElement;
  mobileDrawerTitle?: string;
  className?: string;
  handleClear?: () => void;
  hotelInput?: boolean;
  filterSelection?: string;
  useBlanketWithDropdown?: boolean;
  anchor?: DropdownAnchor;
  dropdownBorderRadius?: keyof typeof border_radius;
}>;

export function Combobox(props: Props) {
  const {
    icon,
    inputText,
    id,
    children,
    label,
    ariaLabel,
    isChildExpanded,
    setIsChildExpanded,
    onClickCta,
    isDisabledCta,
    showMobileControls = false,
    showInput = true,
    onBackgroundClick,
    onDismissClick,
    focusRef,
    pillBorderColor,
    disabled,
    disabledIcon,
    mobileDrawerTitle,
    className,
    handleClear,
    hotelInput,
    filterSelection,
    useBlanketWithDropdown = false,
    anchor = "left",
    dropdownBorderRadius,
  } = props;

  const { isMdScreen } = useScreenMinWidth();

  function handleBlur() {
    if (isMdScreen) {
      setIsChildExpanded(false);
      if (onBackgroundClick) {
        onBackgroundClick();
      }
    }
  }

  function handleKeyPress(event: { key: string }) {
    if (event.key === "Escape") {
      setIsChildExpanded(false);
    }
  }

  function handleClick() {
    if (disabled) return;
    if (props.onClickOpen) {
      props.onClickOpen();
    }
    setIsChildExpanded((prev) => !prev);
  }

  return (
    <>
      {showInput ? (
        <BlurAwayListener
          onBlurAway={handleBlur}
          className={tw("relative lg:h-full", className)}
        >
          <ComboboxInput
            onKeyDown={handleKeyPress}
            icon={icon}
            disabledIcon={disabledIcon}
            inputText={inputText}
            handleInputClick={handleClick}
            id={id}
            label={label}
            ariaLabel={ariaLabel}
            ariaExpanded={isChildExpanded}
            focusRef={focusRef}
            isChildExpanded={isChildExpanded}
            pillBorderColor={pillBorderColor}
            disabled={disabled}
            handleClear={handleClear}
            hotelInput={hotelInput}
            filterSelection={filterSelection}
            isBlanketVisible={useBlanketWithDropdown && isChildExpanded}
          />

          {isMdScreen && isChildExpanded ? (
            <>
              {useBlanketWithDropdown && (
                <Blanket onBlanketClicked={handleBlur} />
              )}
              <DesktopDropDown
                anchor={anchor}
                role="dialog"
                $width={
                  props.isDoubleViewCalendar ? "670px" : props.dropdownWidth
                }
                $maxWidth={props.maxDropDownWidth}
                $borderRadius={dropdownBorderRadius}
                $isBlanketVisible={useBlanketWithDropdown && isChildExpanded}
              >
                {children}
              </DesktopDropDown>
            </>
          ) : null}
        </BlurAwayListener>
      ) : null}

      {!isMdScreen ? (
        <Drawer2
          isOpen={isChildExpanded}
          onBackdropClicked={() => {
            setIsChildExpanded(false);
            if (onBackgroundClick) {
              onBackgroundClick();
            }
          }}
        >
          <Drawer2Title
            onCloseClicked={() => {
              setIsChildExpanded(false);

              if (onDismissClick) {
                onDismissClick();
              }
            }}
          >
            {mobileDrawerTitle}
          </Drawer2Title>
          <Drawer2Content>{children}</Drawer2Content>
          {showMobileControls ? (
            <Drawer2Actions>
              <Button
                className="w-full"
                size="large"
                onClick={() => {
                  if (onClickCta) {
                    onClickCta();
                    setIsChildExpanded(false);
                  }
                }}
                isDisabled={isDisabledCta}
              >
                {props.ctaMessage}
              </Button>
            </Drawer2Actions>
          ) : null}
        </Drawer2>
      ) : null}
    </>
  );
}

const DesktopDropDown = styled(Dropdown)<{
  $maxWidth?: CSSProperties["maxWidth"];
  $borderRadius?: keyof typeof border_radius;
  $isBlanketVisible?: boolean;
  $width?: CSSProperties["width"];
}>`
  margin-top: ${spacing.sm};
  padding: ${spacing.xl};
  width: ${({ $width = "360px" }) => $width};
  max-width: ${(props) => props.$maxWidth};
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  ${({ $isBlanketVisible }) =>
    $isBlanketVisible && `z-index: ${layers.modal};`};

  border-radius: ${(props) => {
    if (props.$borderRadius) {
      return border_radius[props.$borderRadius];
    } else {
      return undefined;
    }
  }};

  &::-webkit-scrollbar-track {
    margin: ${spacing.md} 0;
    background: transparent;
    border: none;
  }

  &::-webkit-scrollbar-thumb {
    border: 3px solid ${color.bg.surface.surface};
    background-color: rgb(199, 199, 199);
    height: 70px;
  }
`;
